import React, { useMemo, useState } from 'react';
import { Typography } from '@mui/material';
import { Col, Form, Row } from 'react-bootstrap';
import useDebounce from '../../hooks/useDebounce';
import { usersTableHead } from '../../static-data/users';
import SearchBar from '../elements/SearchBar';
import TableComponent from '../elements/TableComponent';
import UsersTableAction from './UsersTableAction';
import UserActions from './UserActions';
import { useUserProjects, useUsersList } from '../../query/apiQueries';

const UsersList = () => {
  const [page, setPage] = useState(1);
  const [order, setOrder] = useState('desc');
  const [searchValue, setSearchValue] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [selected, setSelected] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [perPage, setPerPage] = useState(10);

  const currentUser = JSON.parse(localStorage.getItem('user'));
  const debouncedSearchQuery = useDebounce(searchValue, 500);
  const statusFilter = showInactive ? 'inactive' : 'exclude-inactive';

  const {
    data: usersData,
    isLoading: isUsersLoading,
    refetch: refetchUsers,
  } = useUsersList({
    perPage,
    page,
    search: debouncedSearchQuery,
    status: statusFilter,
    includeProject: true,
    projectId: selectedProject,
  });

  const { data: projectData, isLoading: isProjectsLoading } = useUserProjects({
    perPage: 'all',
    page: 1,
    search: null,
    includeTestCases: false,
    includeLinkedProjects: true,
  });
  const users = usersData?.users || [];
  const totalCount = usersData?.count || 0;
  const projects = projectData?.projects || [];

  const orgManagersCount = useMemo(
    () => users.filter((user) => user.role === 'org-manager').length,
    [users]
  );

  const handleSearch = (value) => {
    setSearchValue(value);
    setPage(1);
  };

  const handleProjectChange = (e) => {
    const projectId = e.target.value;
    setSelectedProject(projectId === 'All' ? null : projectId);
    setPage(1);
  };

  const handlePageSizeChange = (e) => {
    const newPerPage = parseInt(e.target.value, 10);
    setPerPage(newPerPage);
    setPage(1);
  };

  return (
    <>
      <Row>
        <Col>
          <Typography variant="h4" className="mb-3">
            Team
          </Typography>
        </Col>
      </Row>
      <Row className="align-items-center">
        <Col className="d-flex">
          <SearchBar
            value={searchValue}
            onChange={(e) => handleSearch(e.target.value)}
            clearSearch={() => handleSearch('')}
            placeholder="Search Users"
          />
        </Col>
        <Col md={3}>
          <Form.Group controlId="projectSelect">
            <Form.Select
              name="projectSelect"
              value={selectedProject || 'All'}
              onChange={handleProjectChange}
              disabled={isProjectsLoading}>
              <option value="All">All Projects</option>
              {projects?.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name.length > 20
                    ? `${project.name.slice(0, 20)}...`
                    : project.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col className="d-flex justify-content-end">
          <UsersTableAction
            fetchUsersData={refetchUsers}
            showInactive={showInactive}
            setShowInactive={setShowInactive}
            seletectItems={selected}
            currentUser={currentUser}
          />
        </Col>
      </Row>

      <Row>
        <Col>
          <TableComponent
            data={users.map((user) => ({
              ...user,
              isRowDisabled: user?.role === 'org-admin',
            }))}
            headerTabs={usersTableHead}
            page={page}
            setPage={setPage}
            perPage={perPage}
            order={order}
            setOrder={setOrder}
            defaultSort="created_at"
            isLoading={isUsersLoading}
            showCheckbox={currentUser?.role === 'org-admin'}
            striped
            totalCount={totalCount}
            showPages
            handlePageChange={setPage}
            tablekey="users"
            setSelected={setSelected}
            selected={selected}
            showCount
            showRowCount={true}
            setPerPage={handlePageSizeChange}
            action={(row) =>
              row?.role !== 'org-admin' ? (
                <UserActions
                  row={row}
                  fetchCustomerList={refetchUsers}
                  currentUser={currentUser}
                  orgManagersCount={orgManagersCount}
                />
              ) : null
            }
          />
        </Col>
      </Row>
    </>
  );
};

export default UsersList;
